import { useMemo, useState } from "react";
import { Flex, Heading, HStack, Icon, List } from "@chakra-ui/react";
import EmptyTable from "assets/icons/medium/empty-table-task.svg?react";
import { Empty } from "components/empty";
import { ImageGalleryModal } from "components/image-gallery/image-gallery-modal";
import { MaintenanceListCardSkeleton } from "components/maintenance-list/maintenance-list-card-skeleton";
import { MaintenanceTaskListItem } from "components/maintenance-list/maintenance-task-list-item";
import { ShowMoreLessButton } from "components/show-more-less-button";
import { TotalCountBadge } from "components/total-count-badge";
import { type MaintenanceTask } from "types/maintenance-task";
import { type MaintenancePaginationMeta } from "types/pagination";

export type LandlordMaintenanceListProps = {
  isLoadingActive: boolean;
  isLoadingPast: boolean;
  activeTasks?: MaintenanceTask[];
  pastTasks?: MaintenanceTask[];
  activePagination?: MaintenancePaginationMeta;
  pastPagination?: MaintenancePaginationMeta;
  onShowMoreActive: () => void;
  onShowMorePast: () => void;
  isFetchingMoreActive?: boolean;
  isFetchingMorePast?: boolean;
};

// initial display when the list are still loading
// it display empty like cards instead of loading screen.
const SKELETON_COUNT = 3;

function hasMorePages(pagination?: MaintenancePaginationMeta) {
  return !!pagination && pagination.current_page < pagination.total_pages;
}

function DisplayEmpty({
  message,
  description,
}: {
  message: string;
  description: string;
}) {
  return (
    <Empty
      icon={<Icon as={EmptyTable} boxSize={"auto"} color={"white"} />}
      emptyMessage={message}
      emptyDesc={description}
    />
  );
}

type MaintenanceSectionProps = {
  testId: string;
  heading: string;
  emptyMessage: string;
  emptyDescription: string;
  tasks: MaintenanceTask[];
  pagination?: MaintenancePaginationMeta;
  isLoading: boolean;
  isFetchingMore?: boolean;
  onShowMore: () => void;
  setCurrentOpenTaskId: (taskId: string) => void;
};

/**
 * The first load has no rows to show yet, so it renders skeletons — the empty
 * state is reserved for a resolved-but-empty list.
 */
function MaintenanceSection({
  testId,
  heading,
  emptyMessage,
  emptyDescription,
  tasks,
  pagination,
  isLoading,
  isFetchingMore,
  onShowMore,
  setCurrentOpenTaskId,
}: MaintenanceSectionProps) {
  return (
    <Flex direction={"column"} gap={4} data-testid={testId}>
      <HStack gap={3}>
        <Heading fontSize={"xl"} as={"h2"}>
          {heading}
        </Heading>
        {isLoading ? null : <TotalCountBadge count={pagination?.total ?? 0} />}
      </HStack>
      {isLoading ? (
        <List spacing={3} data-testid={"maintenance-list-skeleton"}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <MaintenanceListCardSkeleton key={index} />
          ))}
        </List>
      ) : tasks.length === 0 ? (
        <DisplayEmpty message={emptyMessage} description={emptyDescription} />
      ) : (
        <List spacing={3}>
          {tasks.map((task) => (
            <MaintenanceTaskListItem
              key={task.id}
              task={task}
              setCurrentOpenTaskId={setCurrentOpenTaskId}
            />
          ))}
        </List>
      )}
      {!isLoading && hasMorePages(pagination) ? (
        <ShowMoreLessButton onClick={onShowMore} isLoading={isFetchingMore} />
      ) : null}
    </Flex>
  );
}

export function LandlordMaintenanceList({
  isLoadingActive,
  isLoadingPast,
  activeTasks = [],
  pastTasks = [],
  activePagination,
  pastPagination,
  onShowMoreActive,
  onShowMorePast,
  isFetchingMoreActive,
  isFetchingMorePast,
}: LandlordMaintenanceListProps) {
  const [currentOpenTaskId, setCurrentOpenTaskId] = useState<string | null>(
    null,
  );
  const [currentModalImage, setCurrentModalImage] = useState(0);

  // The modal can be opened from either section, so look across both lists.
  const currentOpenTask = useMemo(
    () =>
      [...activeTasks, ...pastTasks].find(
        (task) => task.id === currentOpenTaskId,
      ),
    [currentOpenTaskId, activeTasks, pastTasks],
  );

  return (
    <>
      <MaintenanceSection
        testId={"active-requests"}
        heading={"Active requests"}
        emptyMessage={"Active Requests"}
        emptyDescription={
          "There are currently no active requests for this property"
        }
        tasks={activeTasks}
        pagination={activePagination}
        isLoading={isLoadingActive}
        isFetchingMore={isFetchingMoreActive}
        onShowMore={onShowMoreActive}
        setCurrentOpenTaskId={setCurrentOpenTaskId}
      />
      <MaintenanceSection
        testId={"past-requests"}
        heading={"Past requests"}
        emptyMessage={"Past Requests"}
        emptyDescription={
          "There are currently no past requests for this property"
        }
        tasks={pastTasks}
        pagination={pastPagination}
        isLoading={isLoadingPast}
        isFetchingMore={isFetchingMorePast}
        onShowMore={onShowMorePast}
        setCurrentOpenTaskId={setCurrentOpenTaskId}
      />
      <ImageGalleryModal
        isOpen={!!currentOpenTaskId}
        onClose={() => {
          setCurrentOpenTaskId(null);
          setCurrentModalImage(0);
        }}
        images={currentOpenTask?.images ?? []}
        currentModalImage={currentModalImage}
        setCurrentModalImage={setCurrentModalImage}
      />
    </>
  );
}
